/**
 * security.js — Utilitários de segurança da aplicação Lumenfall University
 * Implementa: hashing de senha, sanitização de input, validação de URL e rate limiting.
 */

// Salt interno para o hash (nunca exposto ao usuário)
const HASH_SALT = 'lumenfall_university_2026_sec_salt';

/**
 * Gera um hash SHA-256 da senha com salt.
 * Usa a Web Crypto API nativa do browser (sem dependências externas).
 * @param {string} password
 * @returns {Promise<string>} hash hexadecimal de 64 caracteres
 */
export async function hashPassword(password) {
  const encoder = new TextEncoder();
  const data = encoder.encode(String(password) + HASH_SALT);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Verifica se uma string parece ser um hash SHA-256 (64 hex chars).
 * Usado para detectar contas criadas antes da migração de segurança.
 * @param {string} str
 * @returns {boolean}
 */
export function isHashed(str) {
  return typeof str === 'string' && /^[0-9a-f]{64}$/.test(str);
}

/**
 * Sanitiza texto removendo caracteres perigosos e limitando o tamanho.
 * @param {string} text
 * @param {number} maxLength
 * @returns {string}
 */
export function sanitizeText(text, maxLength = 200) {
  if (typeof text !== 'string') return '';
  return text
    .replace(/[<>"'`]/g, '') // remove caracteres de injeção HTML/JS
    .trim()
    .slice(0, maxLength);
}

/**
 * Valida se uma URL é HTTPS válida (previne javascript: e data: no src de imagens).
 * @param {string} url
 * @returns {boolean}
 */
export function isValidHttpsUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Valida campos de formulário de cadastro.
 * @param {object} data
 * @returns {string|null} mensagem de erro ou null se válido
 */
export function validateRegistrationData(data) {
  const name = sanitizeText(data.fullName || '', 100);
  const id = sanitizeText(data.citizenId || '', 30);
  const phone = sanitizeText(data.phone || '', 20);
  const pwd = data.password || '';

  if (name.length < 3) return 'Nome completo deve ter pelo menos 3 caracteres.';
  if (id.length < 2) return 'ID do Cidadão inválido (mínimo 2 caracteres).';
  if (phone.length !== 6) return 'O telefone deve ter exatamente 6 dígitos.';
  if (pwd.length < 6) return 'A senha deve ter pelo menos 6 caracteres.';
  if (pwd.length > 128) return 'Senha muito longa (máximo 128 caracteres).';
  if (data.profilePicture && !isValidHttpsUrl(data.profilePicture)) {
    return 'O link da foto deve ser uma URL válida começando com https://';
  }
  return null;
}

// ─── Rate Limiting (baseado em sessionStorage) ───────────────────────────────
export const MAX_LOGIN_ATTEMPTS = 5;
export const LOCKOUT_DURATION_MS = 30_000; // 30 segundos

export function getRateLimitState() {
  const attempts = parseInt(sessionStorage.getItem('lf_attempts') || '0', 10);
  const blockedUntil = parseInt(sessionStorage.getItem('lf_blocked_until') || '0', 10);
  return { attempts, blockedUntil };
}

export function incrementLoginAttempt() {
  const { attempts } = getRateLimitState();
  const next = attempts + 1;
  sessionStorage.setItem('lf_attempts', String(next));
  if (next >= MAX_LOGIN_ATTEMPTS) {
    const until = Date.now() + LOCKOUT_DURATION_MS;
    sessionStorage.setItem('lf_blocked_until', String(until));
    sessionStorage.setItem('lf_attempts', '0');
  }
  return next;
}

export function resetLoginAttempts() {
  sessionStorage.removeItem('lf_attempts');
  sessionStorage.removeItem('lf_blocked_until');
}

export function isLoginBlocked() {
  const { blockedUntil } = getRateLimitState();
  if (!blockedUntil) return { blocked: false, secondsLeft: 0 };
  if (Date.now() < blockedUntil) {
    return { blocked: true, secondsLeft: Math.ceil((blockedUntil - Date.now()) / 1000) };
  }
  // Expirou — limpar
  sessionStorage.removeItem('lf_blocked_until');
  return { blocked: false, secondsLeft: 0 };
}
